import React, { useContext, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import UserContext from 'context/user-context'
import AccessManagementAppContext from 'context/access-management-app-context'
import AuthenticatedAccessManagement from './authenticated-access-management'
import AuthenticatedUser from './authenticated-user'
import AuthenticatedDatum from './authenticated-datum'
import {
  ACCESS_MANAGEMENT_ROUTES,
  USER_ROUTES,
  DATUM_ROUTES,
  pathMatches
} from './app-routes'

function RenderApp() {
  const currentUser = useContext(UserContext)
  const { currentApp, setCurrentApp } = useContext(AccessManagementAppContext)
  const location = useLocation()

  const currentPath = location.pathname
  const isOnAccessManagementRoute = pathMatches(currentPath, ACCESS_MANAGEMENT_ROUTES)
  const isOnUserRoute = pathMatches(currentPath, USER_ROUTES)
  const isOnDatumRoute = pathMatches(currentPath, DATUM_ROUTES)

  // Determine the correct app based on the current route, synchronously
  // so the right app component renders before any catch-all redirects fire.
  // Note: 'sod' is the backend app key for Access Management.
  const resolvedApp = (() => {
    if (isOnDatumRoute && currentUser.has_datum_app) return 'datum'
    if (isOnAccessManagementRoute && currentUser.has_sod_app) return 'sod'
    if (isOnUserRoute && currentUser.has_analytics_app) return null
    return currentApp
  })()

  useEffect(() => {
    if (resolvedApp !== currentApp) {
      setCurrentApp(resolvedApp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedApp])

  // Force Access Management-only users to always be in the Access Management app
  useEffect(() => {
    if (currentUser.has_sod_app && !currentUser.has_analytics_app && currentApp !== 'sod') {
      setCurrentApp('sod')
    }
  }, [currentUser, currentApp, setCurrentApp])

  // If the user only has datum app access, route to datum
  if (currentUser.has_datum_app && !currentUser.has_sod_app && !currentUser.has_analytics_app) {
    return <AuthenticatedDatum />
  }

  // If resolved app is datum and user has datum access
  if (resolvedApp === 'datum' && currentUser.has_datum_app) {
    return <AuthenticatedDatum />
  }

  // Determine Access Management vs Analytics
  const isAccessManagementApp =
    currentUser.has_sod_app &&
    (resolvedApp === 'sod' || (!currentUser.has_analytics_app))

  return <>{isAccessManagementApp ? <AuthenticatedAccessManagement /> : <AuthenticatedUser />}</>
}

export default RenderApp
